import React, { useEffect, useState, useRef } from "react";
import { AlertCircle, X, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { SLA } from "@/constants/testIds";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * Polls /api/sla/breached every 45s. When new WhatsApp inquiries breach the
 * 15-minute threshold, shows a dismissible banner + fires a browser
 * notification.
 */
export default function SlaNudge() {
  const [count, setCount] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const seenIds = useRef(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      // request lazily on first mount
      try {
        Notification.requestPermission();
      } catch (err) {
        console.warn("Saheli SLA: Notification.requestPermission failed", err);
      }
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/sla/breached?minutes=15");
        if (cancelled) return;
        setCount(data.count || 0);
        // notify only for freshly-seen ids
        const fresh = (data.inquiries || []).filter(
          (i) => !seenIds.current.has(i.id),
        );
        fresh.forEach((i) => seenIds.current.add(i.id));
        if (fresh.length > 0 && !document.hidden) {
          if ("Notification" in window && Notification.permission === "granted") {
            fresh.slice(0, 3).forEach((i) => {
              try {
                new Notification("Saheli — WhatsApp reply overdue", {
                  body: `${i.customer_name}: ${(i.message || "").slice(0, 90)}`,
                  icon: "/favicon.ico",
                });
              } catch (err) {
                console.warn("Saheli SLA: browser notification failed", err);
              }
            });
          } else {
            toast.warning(
              `${fresh.length} WhatsApp ${fresh.length === 1 ? "inquiry needs" : "inquiries need"} a reply`,
              { duration: 8000 },
            );
          }
          setDismissed(false);
        }
      } catch (err) {
        console.warn("Saheli SLA: /sla/breached poll failed", err);
      }
    };
    poll();
    const t = setInterval(poll, 45000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (count === 0 || dismissed) return null;

  return (
    <div
      data-testid={SLA.banner}
      className="fixed bottom-4 right-4 z-50 paper-card border-terracotta/40 bg-white shadow-md p-4 max-w-sm animate-fade-up"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-terracotta/10 text-terracotta grid place-items-center shrink-0">
          <Bell className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg text-stone-900">
            <span data-testid={SLA.count}>{count}</span>{" "}
            WhatsApp {count === 1 ? "inquiry" : "inquiries"} awaiting reply
          </div>
          <div className="text-xs text-stone-500 mt-0.5">
            More than 15 minutes since it arrived.
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={() => {
                navigate("/inquiries");
                setDismissed(true);
              }}
              className="text-xs font-medium text-terracotta hover:underline"
            >
              Open inbox →
            </button>
            <button
              data-testid={SLA.dismiss}
              onClick={() => setDismissed(true)}
              className="ml-auto text-xs text-stone-400 hover:text-stone-700"
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-stone-400 hover:text-stone-700"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
